/* eslint-disable no-extra-boolean-cast */
import { datadogRum } from '@datadog/browser-rum';
import { Box, Button, Grid, Hidden, Link, Typography, useTheme } from '@mui/material';
import { get as _get } from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { converseNext, deleteDraft } from '../../../api/converseApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import {
  getAuthKey,
  getClaimCreationProductType,
  getClaimRisks,
  getServicingOpCo,
  getSessionId,
  getUrlKey,
  getUserData,
  userIsAdjuster,
} from '../../../app/store/combinedSelectors';
import {
  CLAIMS_RETRIEVAL_ISSUE_MESSAGE,
  contactUsToFileClaimText,
  deleteBtn,
  DeleteConfirmationConstants,
  fileAClaimBtn,
  GenericModalTopMargin,
  hiddenClaimsText,
  resumeBtn,
  viewDetailsBtn,
} from '../../../Constants/Constants';
import { setupPostFnol } from '../../../services/SetupPostFnol.service';
import { ClaimSummary, GenericProductType } from '../../../types/type';
import { getClaimDescription, getPrimaryNamedInsuredFullName } from '../../../utils/Policy-Util';
import { getClaimType, isPolicyTypeAuto } from '../../../utils/Utilities';
import ClaimDescriptionLink from '../../shared/ClaimDescriptionLink/ClaimDescriptionLink';
import ProgressTrackerMini from '../../shared/ProgressTracker/ProgressTrackerMini';
import ClaimList from '../claims-list/ClaimsList';

import { getTime } from 'date-fns';
import { getClaims } from '../../../api/getClaimsApi';
import { useModalVisibility } from '../../../app/hooks/useModalVisibility';
import {
  addDriver,
  addFnolReporter,
  addFnolRisk,
  saveDateLocation,
  setErsClaim,
  setGlassClaim,
  updateDraftPayload,
} from '../../../app/store/ClaimCreation/ClaimCreationSlice';
import { deleteDraftClaim, setClaimsRetrievalIssue, updateClaimsState } from '../../../app/store/Claims/ClaimsSlice';
import { updateAuthToken } from '../../../app/store/ContextRouter/ContextRouterActions';
import {
  resetAvailableProgressStagesState,
  resetEligibleServiceState,
  resetSelectedProvidersState,
} from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { setupDraftResumeFields } from '../../../services/SetupFnol.service';
import { hasClaimsRetrievalIssue } from '../../../utils/ClaimsResponse-Util';
import { DatadogErrorTracker } from '../../../utils/DatadogErrorTracker';
import { getPolicySource } from '../../../utils/PolicySource-Util';
import { getPrettyClaimNumber, postMessageToParentApp } from '../../../utils/Utilities';
import ErrorBanner from '../../shared/ErrorBanner/ErrorBanner';
import GenericModal from '../../shared/GenericModal/GenericModal';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';

interface LoadTypeInfo {
  isClaimRefreshNeeded: boolean | null;
}

export default function ClaimsOverview(props: any) {
  const theme = useTheme();
  const claimsData = useAppSelector((state) => state.claims);
  const sortedDraftClaims = getSortedClaimsByDate(claimsData.draftClaims);
  const policies = useAppSelector((state) => state.policies.policies);

  // Enrich Workers Comp claims with named insured from policy data
  const enrichClaimsWithNamedInsured = (claims: any[]) => {
    if (!claims || !Array.isArray(claims)) {
      return claims;
    }
    return claims.map((claim: any) => {
      const isWorkersComp = getClaimType(_get(claim, 'policyType')) === GenericProductType.WorkersComp;
      if (isWorkersComp && claim.policyNumber) {
        const matchingPolicy = policies.find((policy: any) => _get(policy, 'policyNumber') === claim.policyNumber);
        if (matchingPolicy) {
          const fullName = getPrimaryNamedInsuredFullName(matchingPolicy);
          if (fullName && !claim.businessName) {
            return { ...claim, businessName: fullName };
          }
        }
      }
      return claim;
    });
  };

  const sortedOpenClaims = enrichClaimsWithNamedInsured(getSortedClaimsByDate(claimsData.openClaims));
  const sortedClosedClaims = enrichClaimsWithNamedInsured(getSortedClaimsByDate(claimsData.closedClaims));
  const hasHiddenClaims = claimsData.hasHiddenClaims;
  const claimRisks = useSelector(getClaimRisks);
  const isAdjuster = useSelector(userIsAdjuster);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [modalDraft, setModalDraft] = React.useState({
    claimNumber: '',
    riskName: '',
    savedDate: '',
  });
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  // Calls the custom hook useModalVisibility, passing in the setModalLoadingOpen function.The hook returns a new function, setModalVisibility, which can be used to show or hide the modal.
  const setModalVisibility = useModalVisibility(setModalLoadingOpen);
  const [showErrorBanner, setShowErrorBanner] = React.useState(false);
  const dispatch = useAppDispatch();

  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const customerData = useSelector(getUserData);
  const opco = useSelector(getServicingOpCo);
  const typeOfClaim = useSelector(getClaimCreationProductType);
  const isAuto = typeOfClaim === GenericProductType.Auto;
  const navigate = useNavigate();

  // Check if all policies are restricted types (Workers Comp, Commercial Auto, Commercial Property)
  const shouldHideFileClaimButton = React.useMemo(() => {
    console.log('🔍 Debug - Checking shouldHideFileClaimButton');
    console.log('🔍 Debug - Policies count:', policies?.length);
    console.log('🔍 Debug - Policies:', policies);

    if (!policies || policies.length === 0) {
      console.log('🔍 Debug - No policies, hide button');
      return true; // Hide button if no policies
    }

    // Get all risks from all policies
    const allRisks: any[] = [];
    policies.forEach((policy: any) => {
      const risks = _get(policy, 'risks', []);
      console.log('🔍 Debug - Policy risks:', risks);
      allRisks.push(...risks);
    });

    console.log('🔍 Debug - Total risks count:', allRisks.length);
    console.log('🔍 Debug - All risks:', allRisks);

    if (allRisks.length === 0) {
      console.log('🔍 Debug - No risks, hide button');
      return true; // Hide button if no risks
    }

    // Check if all risks are restricted types
    const isAllRestricted = allRisks.every((risk: any) => {
      const riskType = _get(risk, 'generalizedProductType');
      console.log('🔍 Debug - Risk type:', riskType, 'Risk:', risk);
      return (
        riskType === GenericProductType.WorkersComp ||
        riskType === GenericProductType.CommercialAuto ||
        riskType === GenericProductType.CommercialProperty
      );
    });

    console.log('🔍 Debug - All risks restricted?', isAllRestricted);
    console.log('🔍 Debug - Should hide button?', isAllRestricted);
    return isAllRestricted;
  }, [policies]);
  const { state } = useLocation();
  const initialRefreshNeeded = (state as LoadTypeInfo)?.isClaimRefreshNeeded ?? false;
  const [claimRefreshNeeded, setClaimRefreshNeeded] = React.useState(initialRefreshNeeded);
  const cardStyle = {
    border: `${theme.custom.styles.defaultBorderWidth} solid ${theme.custom.colors.defaultBorderColor}`,
    borderRadius: theme.custom.styles.defaultBorderRadius,
    margin: '1rem 0rem 1rem 0rem',
  };

  React.useEffect(() => {
    /**
     * Reset updateSelectedProvidersState slice of servicesSlice
     * This will handle not storing selected providers of previous claims
     */
    dispatch(resetSelectedProvidersState());

    /**
     * Reset glass and ERS claim flags when returning to claims overview
     * This ensures flags don't persist from previous claim sessions
     */
    dispatch(setGlassClaim({ isGlassClaim: false }));
    dispatch(setErsClaim({ isErsClaim: false }));

    if (claimRefreshNeeded) {
      initiateClaimsByPolicyCall();
      setClaimRefreshNeeded(false); // Consume the flag so it doesn't run again
    }
    async function initiateClaimsByPolicyCall() {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
      setModalVisibility(true); // Show modal
      const claimsByPolicyResponse = await getClaims(urlKey, sessionId, authKey);
      setModalVisibility(false); // Hide modal
      // Set on every completed call, so a healthy refetch clears a banner left from an earlier one.
      const claimsIssue = hasClaimsRetrievalIssue(claimsByPolicyResponse);
      dispatch(setClaimsRetrievalIssue(claimsIssue));

      if (claimsIssue) {
        DatadogErrorTracker.trackClaimsRetrievalIssue(claimsByPolicyResponse);
      }

      // A rejected fetch resolves to an Error with no error property, so check for the data array.
      if (claimsByPolicyResponse && !claimsByPolicyResponse.error && Array.isArray(claimsByPolicyResponse.data)) {
        dispatch(updateClaimsState({ response: claimsByPolicyResponse.data }));
      } else {
        postMessageToParentApp(
          PostMessageCodes.DIGITAL_CLAIM_V2_ALL_CLAIMS_RETRIEVAL_ERROR,
          '',
          claimsByPolicyResponse
        );
      }
      return claimsByPolicyResponse;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authKey, dispatch, claimRefreshNeeded, urlKey]);

  function getSortedClaimsByDate(claims?: ClaimSummary[]) {
    if (claims && claims.length) {
      const arrayToSort = [...claims];
      const sortedClaims = arrayToSort.sort((obj1: any, obj2: any) => {
        return getTime(new Date(obj2.lossDate)) - getTime(new Date(obj1.lossDate));
      });
      return sortedClaims;
    } else {
      return;
    }
  }

  function handleNext() {
    if (!!isAdjuster) {
      navigate('/reporter-info');
    } else {
      navigate('/risk-selection');
    }
  }

  function handleCloseButtonClick() {
    setDeleteModalOpen(false);
  }

  function handleConfirmedDelete() {
    deleteConfirmation();
  }

  function handleDelete(row: any) {
    postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
    setModalDraft({
      riskName: row.description ? row.description : getClaimDescription(row),
      savedDate: row.date ? row.date : row.openDate,
      claimNumber: row.name ? row.name : row.claimNumber,
    });
    setDeleteModalOpen(true);
  }

  async function deleteConfirmation() {
    setShowErrorBanner(false);
    setDeleteModalOpen(false);
    setModalVisibility(true);
    const deleteResponse = await deleteDraft(urlKey, sessionId, authKey, modalDraft.claimNumber);
    setModalVisibility(false); // Hide modal
    if (_get(deleteResponse, 'status.statusCode', '') >= 200) {
      dispatch(updateAuthToken(_get(deleteResponse, 'data.updatedAuthToken', authKey)));
      dispatch(
        deleteDraftClaim({
          claimNumber: modalDraft.claimNumber,
        })
      );
    } else {
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_V2_DRAFT_CLAIM_DELETE_ERROR,
        modalDraft.claimNumber,
        deleteResponse
      );
      setShowErrorBanner(true);
    }
  }

  async function resumeClaim(claim: any) {
    //Sent message to scroll the parent app to top
    postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
    setShowErrorBanner(false);
    setModalVisibility(true); // Show modal
    const lob = isPolicyTypeAuto(claim) ? 'auto' : 'property';
    const claimNumber = _get(claim, 'name', _get(claim, 'claimNumber', '')).replace(/-/g, '');
    datadogRum.setGlobalContextProperty('draftClaimNumber', claimNumber); // Adding this allows us to filter sessions based upon @context.draftClaimNumber value in DataDog Session Explorer

    // Send the policy-source header in requests to Converse to enable differentiation of question set retrieval between CAH AUTO and other OPCO lines of business (LOBs).
    let policySource = '';
    if (isPolicyTypeAuto(claim)) {
      // Get Complete Draft
      const completeDraft = sortedDraftClaims?.find((draft) => {
        return draft.claimNumber === claimNumber;
      });
      // Get Policy Source
      policySource = getPolicySource(_get(completeDraft, 'policyNumber', ''), policies);
    }

    const converseResponse = await converseNext(urlKey, sessionId, authKey, lob, opco, claimNumber, {}, policySource);
    setModalVisibility(false); // Hide modal

    if (_get(converseResponse, 'status.statusCode', '') >= 200) {
      if (isGlassEnd(converseResponse)) {
        dispatch(setGlassClaim({ isGlassClaim: true }));
        navigate('/glass-call-us');
      } else if (isErsEnd(converseResponse)) {
        dispatch(setErsClaim({ isErsClaim: true }));
        navigate('/ers-call-us');
      } else {
        const claimCreationState = setupDraftResumeFields(converseResponse.data.response, claimRisks);
        dispatch(addFnolRisk({ riskInfo: claimCreationState.riskInfo }));
        dispatch(addFnolReporter({ reporterInfo: claimCreationState.reporterInfo }));
        dispatch(addDriver({ driverInfo: claimCreationState.driverInfo }));
        dispatch(
          saveDateLocation({
            dateLocationInfo: claimCreationState.dateLocationInfo,
          })
        );

        dispatch(updateAuthToken(converseResponse.data.updatedAuthToken));
        dispatch(
          updateDraftPayload({
            draftPayload: converseResponse.data.response.draftClaimInfo,
          })
        );
        navigate('/dynamic-question', {
          state: {
            activeQuestion: converseResponse.data.response,
            draftClaimNumber: _get(claim, 'name', _get(claim, 'claimNumber', '')).replace(/-/g, ''),
            isAuto: isPolicyTypeAuto(claim),
            reviewSheet: _get(converseResponse, 'data.response.question.reviewSheet'),
          },
        });
      }
    } else {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CONVERSE_NEXT_ERROR, claimNumber, converseResponse);
      setShowErrorBanner(true);
    }
  }

  function isGlassEnd(converseResponse: any): boolean {
    return (
      converseResponse?.data?.response?.question?.id === 'end' &&
      converseResponse?.data?.response?.question?.questionType === 'redirect_to_glass_view'
    );
  }

  function isErsEnd(converseResponse: any): boolean {
    return (
      converseResponse?.data?.response?.question?.id === 'end' &&
      converseResponse?.data?.response?.question?.questionType === 'redirect_to_roadside_view'
    );
  }

  async function goToDetail(claim: any) {
    // Reset eligibility service state and Available Progress Stages state
    dispatch(resetEligibleServiceState());
    dispatch(resetAvailableProgressStagesState());

    //Sent message to scroll the parent app to top
    postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
    setShowErrorBanner(false);

    setModalVisibility(true); // Show modal
    const claimNumber = _get(claim, 'name', _get(claim, 'claimNumber', '')).replace(/-/g, '');

    const successfulSetup = await setupPostFnol(
      customerData,
      claimNumber,
      urlKey,
      sessionId,
      authKey,
      true,
      opco,
      'claims-overview'
    );
    setModalVisibility(false); // Hide modal
    if (
      successfulSetup === GenericProductType.Auto ||
      successfulSetup === GenericProductType.Home ||
      successfulSetup === GenericProductType.CommercialAuto ||
      successfulSetup === GenericProductType.CommercialProperty ||
      successfulSetup === GenericProductType.WorkersComp
    ) {
      navigate('/claim-detail');
    } else {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CLAIM_DETAIL_FAIL_ERROR, claimNumber, successfulSetup);
      setShowErrorBanner(true);
    }
  }

  function goToVehicles(claimNumber: string) {
    navigate('/vehicles', { state: { claimNumber } });
  }

  const deleteModal = (
    <GenericModal
      title={`Delete Saved Draft on ${modalDraft.riskName}?`}
      description={`\n\nIf you delete this draft, any information you've entered will be lost.`}
      primaryButtonText={DeleteConfirmationConstants.PRIMARY_BUTTON_TEXT}
      secondaryButtonText={DeleteConfirmationConstants.SECONDARY_BUTTON_TEXT}
      verticalPositionForMobile={GenericModalTopMargin}
      theme={theme}
      alertBoxStyle={theme.custom.styles.genericModalDestructiveAlertBoxStyle}
      closeButtonActionHandler={handleCloseButtonClick}
      primaryButtonActionHandler={handleConfirmedDelete}
      secondaryButtonActionHandler={handleCloseButtonClick}
    ></GenericModal>
  );

  return (
    <Box>
      {/* Degraded claims retrieval - the list below may be incomplete or empty. */}
      {/* No reload button, reloading returns the same incomplete list. */}
      {claimsData.hasClaimsRetrievalIssue && (
        <ErrorBanner message={CLAIMS_RETRIEVAL_ISSUE_MESSAGE} showReloadButton={false}></ErrorBanner>
      )}
      {showErrorBanner && <ErrorBanner claimNumber={modalDraft.claimNumber}></ErrorBanner>}
      {!!deleteModalOpen && deleteModal}
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
      <Grid container sx={{ marginTop: '1rem' }}>
        <Grid item xs={12}>
          <Typography sx={theme.custom.styles.confirmationSubHeader}>
            When the unexpected happens, we're here for you.
          </Typography>
        </Grid>

        {!shouldHideFileClaimButton ? (
          <Grid item xs={12} sx={{ marginTop: '2em' }}>
            <Button
              onClick={handleNext}
              sx={{
                ...theme.custom.styles.ctaNextButton,
                '&.MuiButtonBase-root:hover': {
                  bgcolor: theme.custom.colors.primaryButtonHoverColor,
                },
              }}
            >
              {fileAClaimBtn}
            </Button>
          </Grid>
        ) : (
          <Grid item xs={12} sx={{ marginTop: '2em' }}>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 400,
                color: theme.custom.colors.textSecondary,
                textAlign: 'left',
              }}
            >
              {contactUsToFileClaimText}
            </Typography>
          </Grid>
        )}

        <Hidden smUp>
          {!!sortedDraftClaims && sortedDraftClaims.length > 0 && (
            <>
              <Grid item xs={12} sx={{ marginTop: '2em' }}>
                <Typography sx={theme.custom.styles.titleType3}>
                  Saved for later ({sortedDraftClaims.length})
                </Typography>
              </Grid>

              {sortedDraftClaims.map((draft: any) => (
                <Grid container sx={cardStyle} key={draft.name ? draft.name : draft.claimNumber}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      px: '1em',
                      paddingTop: '1em',
                    }}
                  >
                    <Link
                      component="button"
                      underline="hover"
                      sx={{
                        ...theme.custom.styles.titleType4,
                        textAlign: 'left',
                        whiteSpace: 'pre-line',
                      }}
                      onClick={() => resumeClaim(draft)}
                    >
                      Saved Draft
                    </Link>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      <ClaimDescriptionLink
                        description={getClaimDescription(draft)}
                        maskInDatadog={getClaimType(_get(draft, 'policyType')) === GenericProductType.WorkersComp}
                      />
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{
                      paddingLeft: '1em',
                      paddingTop: '1em',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body1">
                      <b>Incident Date</b>
                    </Typography>
                  </Grid>

                  <Grid item xs={8} sx={{ paddingTop: '1em' }}>
                    <Typography variant="body1">{draft.lossDate}</Typography>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{
                      paddingLeft: '1em',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body1">
                      <b> Status</b>
                    </Typography>
                  </Grid>

                  <Grid item xs={8} sx={{ paddingTop: '1em' }}>
                    <Typography style={{ width: '33%', textAlign: 'left' }} variant="body1">
                      Submit
                    </Typography>

                    <ProgressTrackerMini marginLeft={'0%'} step={'Submit'} name={draft.name} />
                  </Grid>
                  <Grid
                    item
                    borderBottom={`${theme.custom.styles.defaultBorderWidth} solid ${theme.custom.colors.defaultBorderColor}`}
                    sx={{ width: '100%', paddingTop: '1rem' }}
                  ></Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      paddingTop: '1em',
                      paddingBottom: '1em',
                      paddingRight: '1em',
                      flexDirection: 'row',
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      onClick={() => handleDelete(draft)}
                      sx={{
                        ...theme.custom.styles.genericDestructiveTertiaryButton,
                      }}
                    >
                      {deleteBtn}
                    </Button>

                    <Button
                      onClick={() => resumeClaim(draft)}
                      sx={{
                        ...theme.custom.styles.genericTertiaryButton,
                      }}
                    >
                      {resumeBtn}
                    </Button>
                  </Grid>
                </Grid>
              ))}
            </>
          )}

          {!!sortedOpenClaims && sortedOpenClaims.length > 0 && (
            <>
              <Grid item xs={12} sx={{ marginTop: '2em' }}>
                <Typography sx={theme.custom.styles.titleType3}>Active ({sortedOpenClaims.length})</Typography>
              </Grid>

              {sortedOpenClaims.map((claim: any) => (
                <Grid container sx={cardStyle} key={claim.name ? claim.name : claim.claimNumber}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      px: '1em',
                      paddingTop: '1em',
                    }}
                  >
                    <Link
                      component="button"
                      underline="hover"
                      sx={{
                        ...theme.custom.styles.titleType4,
                        textAlign: 'left',
                        whiteSpace: 'pre-line',
                      }}
                      onClick={() => {
                        goToDetail(claim);
                      }}
                    >
                      Claim {getPrettyClaimNumber(claim.claimNumber)}
                    </Link>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      <ClaimDescriptionLink
                        description={getClaimDescription(claim)}
                        onMultipleVehiclesClick={() => goToVehicles(claim.claimNumber)}
                        maskInDatadog={getClaimType(_get(claim, 'policyType')) === GenericProductType.WorkersComp}
                      />
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{
                      paddingLeft: '1em',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body1">
                      <b>Incident Date</b>
                    </Typography>
                  </Grid>

                  <Grid item xs={8} sx={{ paddingTop: '1em', paddingBottom: '1em' }}>
                    <Typography variant="body1">{claim.lossDate}</Typography>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{
                      paddingLeft: '1em',

                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body1">
                      <b> Status</b>
                    </Typography>
                  </Grid>

                  <Grid item xs={8}>
                    <Typography style={{ width: '33%', textAlign: 'left' }} variant="body1">
                      Submit
                    </Typography>

                    <ProgressTrackerMini marginLeft={'0%'} step={'Submit'} name={claim.name} />
                  </Grid>
                  <Grid
                    item
                    borderBottom={`${theme.custom.styles.defaultBorderWidth} solid ${theme.custom.colors.defaultBorderColor}`}
                    sx={{ width: '100%', paddingTop: '1rem' }}
                  ></Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      paddingTop: '1em',
                      paddingBottom: '1em',
                      paddingRight: '1em',
                      flexDirection: 'row',
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      onClick={() => {
                        goToDetail(claim);
                      }}
                      sx={{
                        ...theme.custom.styles.genericTertiaryButton,
                      }}
                    >
                      {viewDetailsBtn}
                    </Button>
                  </Grid>
                </Grid>
              ))}
            </>
          )}

          {!!sortedClosedClaims && sortedClosedClaims.length > 0 && (
            <>
              <Grid item xs={12} sx={{ marginTop: '2em' }}>
                <Typography sx={theme.custom.styles.titleType3}>Closed ({sortedClosedClaims.length})</Typography>
              </Grid>

              {sortedClosedClaims.map((claim: any) => (
                <Grid container sx={cardStyle} key={claim.name ? claim.name : claim.claimNumber}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      px: '1em',
                      paddingTop: '1em',
                      paddingBottom: '1em',
                    }}
                  >
                    <Link
                      component="button"
                      underline="hover"
                      sx={{
                        ...theme.custom.styles.titleType4,
                        textAlign: 'left',
                        whiteSpace: 'pre-line',
                      }}
                      onClick={() => {
                        goToDetail(claim);
                      }}
                    >
                      Claim {claim.claimNumber}
                    </Link>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      <ClaimDescriptionLink
                        description={getClaimDescription(claim)}
                        onMultipleVehiclesClick={() => goToVehicles(claim.claimNumber)}
                        maskInDatadog={getClaimType(_get(claim, 'policyType')) === GenericProductType.WorkersComp}
                      />
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{
                      paddingLeft: '1em',
                      paddingTop: '1em',
                      paddingBottom: '1em',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body1">
                      <b>Incident Date</b>
                    </Typography>
                  </Grid>

                  <Grid item xs={8} sx={{ paddingTop: '1em', paddingBottom: '1em' }}>
                    <Typography variant="body1">{claim.openDate}</Typography>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{
                      paddingLeft: '1em',
                      paddingTop: '1em',
                      paddingBottom: '1em',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body1">
                      <b> Status</b>
                    </Typography>
                  </Grid>

                  <Grid item xs={8} sx={{ paddingTop: '1em', paddingBottom: '1em' }}>
                    <Typography style={{ width: '33%', textAlign: 'left' }} variant="body1">
                      Closed
                    </Typography>

                    <ProgressTrackerMini marginLeft={'0%'} step={'Closed'} name={claim.name} />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{
                      paddingTop: '1em',
                      paddingBottom: '1em',
                      px: '1em',
                      flexDirection: 'row',
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      onClick={() => {
                        goToDetail(claim);
                      }}
                      sx={{
                        ...theme.custom.styles.genericTertiaryButton,
                      }}
                    >
                      {viewDetailsBtn}
                    </Button>
                  </Grid>
                </Grid>
              ))}
            </>
          )}
        </Hidden>

        <Hidden smDown>
          {!!sortedDraftClaims && sortedDraftClaims.length > 0 && (
            <>
              <Grid item xs={12} sx={{ marginTop: '2em' }}>
                <Typography sx={theme.custom.styles.titleType3}>
                  Saved for later ({sortedDraftClaims.length})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ClaimList
                  draftsAndFull={false}
                  handleDelete={handleDelete}
                  handleNext={resumeClaim}
                  isDraft={true}
                  claims={sortedDraftClaims}
                />
              </Grid>
            </>
          )}
          {!!sortedOpenClaims && sortedOpenClaims.length > 0 && (
            <>
              <Grid item xs={12} sx={{ marginTop: '2em' }}>
                <Typography sx={theme.custom.styles.titleType3}>Active ({sortedOpenClaims.length})</Typography>
              </Grid>
              <Grid item xs={12}>
                <ClaimList
                  handleNext={(row: any) => goToDetail(row)}
                  onMultipleVehiclesClick={(claimNumber: string) => goToVehicles(claimNumber)}
                  isDraft={false}
                  claims={sortedOpenClaims}
                />
              </Grid>
            </>
          )}
          {!!sortedClosedClaims && sortedClosedClaims.length > 0 && (
            <>
              <Grid item xs={12} sx={{ marginTop: '2em' }}>
                <Typography sx={theme.custom.styles.confirmationSubHeader}>
                  Closed ({sortedClosedClaims.length})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ClaimList
                  handleNext={(row: any) => goToDetail(row)}
                  onMultipleVehiclesClick={(claimNumber: string) => goToVehicles(claimNumber)}
                  isDraft={false}
                  claims={sortedClosedClaims}
                />
              </Grid>
            </>
          )}
          {hasHiddenClaims && <Typography sx={{ marginTop: '2em' }}>{hiddenClaimsText}</Typography>}
        </Hidden>
      </Grid>
    </Box>
  );
}
